import { useRef, useState } from "react";
import { toast } from "sonner";
import { Microphone, Stop, CircleNotch } from "@phosphor-icons/react";
import { transcribe } from "@/lib/api";

export function VoiceButton({ onResult, disabled, testId = "voice-button" }) {
  const [state, setState] = useState("idle");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    if (disabled) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mr = new MediaRecorder(stream);
      mediaRef.current = mr;
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (!blob.size) return setState("idle");
        setState("transcribing");
        try {
          const text = await transcribe(blob);
          text ? onResult(text) : toast.error("Couldn't hear anything, try again");
        } catch {
          toast.error("Transcription failed");
        } finally {
          setState("idle");
        }
      };
      mr.start();
      setState("recording");
    } catch {
      toast.error("Microphone access denied");
      setState("idle");
    }
  };

  const onClick = () => (state === "recording" ? mediaRef.current?.stop() : start());

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      disabled={disabled || state === "transcribing"}
      title={state === "recording" ? "Stop & transcribe" : "Speak instead of typing"}
      className={`flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-medium transition-colors active:scale-95 disabled:opacity-40 ${
        state === "recording" ? "bg-[#ff2d55] text-white" : "bg-black/5 text-ink hover:bg-black/10"
      }`}
    >
      {state === "transcribing" ? (
        <CircleNotch size={15} weight="bold" className="animate-spin" />
      ) : state === "recording" ? (
        <Stop size={15} weight="fill" className="animate-pulse" />
      ) : (
        <Microphone size={15} weight="fill" />
      )}
      {state === "recording" ? "Stop" : state === "transcribing" ? "Listening" : "Speak"}
    </button>
  );
}
